<template lang="html">
  <modal-template
    ref="modal"
    modal-size="sm"
    :title="modalTitle"
    type-modal="bulk_price_update_modal"
  >
    <div class="modal-content__main">
      <template v-if="step === 'form'">
        <d-form @submit.prevent="goToConfirmation">
          <d-form-row>
            <d-col md="12" class="form-group">
              <label for="price-table">Tabela de Preço</label>
              <d-select
                id="price-table"
                class="form-control"
                size="sm"
                v-model="selectedPriceTableId"
                :class="{ 'is-invalid': errors.priceTable }"
              >
                <option :value="null" disabled>Selecione uma tabela...</option>
                <option
                  v-for="type in priceTables"
                  :key="priceTableId(type)"
                  :value="priceTableId(type)"
                >
                  {{ type.name }}
                </option>
              </d-select>
              <d-form-invalid-feedback v-if="errors.priceTable">
                {{ errors.priceTable }}
              </d-form-invalid-feedback>
            </d-col>
          </d-form-row>

          <d-form-row>
            <d-col md="12" class="form-group">
              <label for="percentage">Percentual de reajuste (%)</label>
              <input-percentage
                ref="percentageInput"
                id="percentage"
                model-index="percentage"
                :model="form"
                placeholder="Ex.: 10,5 para +10,5%, -5 para -5%"
                :state="errors.percentage ? 'invalid' : null"
              />
              <d-form-invalid-feedback v-if="errors.percentage">
                {{ errors.percentage }}
              </d-form-invalid-feedback>
            </d-col>
          </d-form-row>

          <d-form-row>
            <d-col md="12" class="form-group">
              <d-checkbox
                :checked="onlyAvailable"
                @change="onlyAvailable = !onlyAvailable"
              >
                Atualizar apenas produtos disponíveis
              </d-checkbox>
            </d-col>
          </d-form-row>
        </d-form>
      </template>

      <template v-else>
        <p class="confirm-message">
          Tem certeza que deseja atualizar
          {{ onlyAvailable ? 'apenas os produtos disponíveis' : 'todos os produtos' }}
          da tabela
          <span class="confirm-highlight">{{ selectedPriceTableName }}</span> com reajuste de
          <span class="confirm-highlight">{{ formattedPercentage }}</span>?
        </p>
        <p class="confirm-warning mb-0">
          <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
          Esta alteração será aplicada em massa
          {{ onlyAvailable ? 'aos produtos disponíveis' : 'a todos os produtos' }}
          da tabela selecionada
          e <span class="confirm-highlight">não poderá ser desfeita</span>.
        </p>
      </template>
    </div>

    <div slot="actions" class="modal-content__control">
      <template v-if="step === 'form'">
        <d-button
          type="button"
          @click="cancel"
          class="btn btn-sm btn-secondary mr-4"
        >
          Cancelar
        </d-button>
        <d-button
          type="button"
          @click="goToConfirmation"
          class="btn btn-sm btn-primary"
        >
          Continuar
        </d-button>
      </template>

      <template v-else>
        <d-button
          type="button"
          @click="backToForm"
          class="btn btn-sm btn-secondary mr-4"
          :disabled="loading"
        >
          Voltar
        </d-button>
        <d-button
          type="button"
          @click="confirmUpdate"
          class="btn btn-sm btn-primary"
          :disabled="loading"
        >
          Confirmar
        </d-button>
      </template>
    </div>
  </modal-template>
</template>

<script>
import { mapActions } from 'vuex';
import ModalTemplate from '_shared/Modal.vue';
import InputPercentage from '_shared/InputPercentage.vue';
import ProductService from '_services/rest/modules/product';

const MODAL_TYPE = 'bulk_price_update_modal';
const MIN_PERCENTAGE = -100;

export default {
  name: 'bulk-price-update-modal',
  components: {
    ModalTemplate,
    InputPercentage,
  },
  props: {
    priceTables: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      step: 'form',
      selectedPriceTableId: null,
      form: {
        percentage: null,
      },
      onlyAvailable: true,
      loading: false,
      errors: {
        priceTable: null,
        percentage: null,
      },
    };
  },
  computed: {
    modalTitle() {
      return this.step === 'form'
        ? 'Atualizar preço em massa'
        : 'Confirmar atualização';
    },
    formattedPercentage() {
      const value = Number(this.form.percentage);
      if (Number.isNaN(value) || this.form.percentage === null) return '';
      const sign = value > 0 ? '+' : '';
      return `${sign}${value}%`;
    },
    selectedPriceTableName() {
      const table = this.priceTables.find(type =>
        this.priceTableId(type) === Number(this.selectedPriceTableId));
      return table ? table.name : '';
    },
  },
  watch: {
    '$store.state.toggle.bulk_price_update_modal': function (isOpen) {
      if (!isOpen) {
        this.resetForm();
      }
    },
  },
  methods: {
    ...mapActions([
      'setToggle',
      'activeAlert',
    ]),
    priceTableId(type) {
      return type.id ? type.id : 0;
    },
    validateForm() {
      this.errors = { priceTable: null, percentage: null };
      let isValid = true;

      if (this.$refs.percentageInput) {
        this.$refs.percentageInput.syncModel();
      }

      if (
        this.selectedPriceTableId === null
        || this.selectedPriceTableId === undefined
        || this.selectedPriceTableId === ''
      ) {
        this.errors.priceTable = 'Selecione uma tabela de preço.';
        isValid = false;
      }

      if (this.form.percentage === null || this.form.percentage === '' || Number.isNaN(Number(this.form.percentage))) {
        this.errors.percentage = 'Informe um percentual válido.';
        isValid = false;
      } else if (Number(this.form.percentage) <= MIN_PERCENTAGE) {
        this.errors.percentage =
          'O percentual deve ser maior que -100%. Valores iguais ou inferiores a -100% zerariam ou tornariam o preço negativo.';
        isValid = false;
      }

      return isValid;
    },
    goToConfirmation() {
      if (!this.validateForm()) return;
      this.step = 'confirm';
    },
    backToForm() {
      this.step = 'form';
    },
    confirmUpdate() {
      if (this.loading) return;
      this.loading = true;

      ProductService.bulkAdjustPriceTable({
        price_table: Number(this.selectedPriceTableId),
        percentage: Number(this.form.percentage),
        only_available: this.onlyAvailable,
      })
        .then(response => {
          this.activeAlert({ message: response.message, theme: 'success' });
          this.$emit('updated');
          this.closeModal();
        })
        .catch(error => {
          this.activeAlert({
            message: `${error.message || 'Erro ao atualizar preços.'}${error.data ? `<br/> ${error.data}` : ''}`,
            theme: 'danger',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancel() {
      this.closeModal();
    },
    closeModal() {
      this.setToggle({ type: MODAL_TYPE, bool: false });
    },
    resetForm() {
      this.step = 'form';
      this.selectedPriceTableId = null;
      this.form = { percentage: null };
      this.onlyAvailable = true;
      this.loading = false;
      this.errors = { priceTable: null, percentage: null };
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-content__control {
  display: flex;
  justify-content: flex-end;
}

.confirm-message {
  text-align: center;
  font-size: 1.1rem;
}

.confirm-highlight {
  font-weight: 600;
}

.confirm-warning {
  text-align: center;
  color: #c79100;

  i {
    margin-right: 0.35rem;
  }
}
</style>
